import { Injectable } from '@angular/core';

export interface TableColumnConfig {
  order: string[];
  hidden: string[];
}

interface StoredTableColumnConfig extends TableColumnConfig {
  /** Fehlt bei Konfigurationen, die vor der benutzerseitigen Steuerung der ID-Spalten entstanden sind. */
  version?: number;
}

const STORAGE_KEY = 'TableColumnConfig';

/** Ab dieser Version enthaelt `hidden` auch die standardmaessig ausgeblendeten Spalten. */
const CONFIG_VERSION = 2;

@Injectable({
  providedIn: 'root',
})
export class TableColumnConfigService {
  getConfig(tableKey: string): StoredTableColumnConfig | null {
    if (!tableKey) return null;

    const allConfigs = this.readAll();
    const scopedKey = this.buildScopedKey(tableKey);
    let config = allConfigs[scopedKey];

    if (!config) {
      // Backward compatibility: read legacy unscoped key and migrate to scoped key.
      const legacyKey = tableKey.trim().toLowerCase();
      const legacy = allConfigs[legacyKey] ?? allConfigs[tableKey];
      if (!legacy) return null;

      config = {
        order: this.normalizeIds(legacy.order),
        hidden: this.normalizeIds(legacy.hidden),
        version: legacy.version,
      };

      allConfigs[scopedKey] = config;
      delete allConfigs[legacyKey];
      delete allConfigs[tableKey];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
    }

    return {
      order: this.normalizeIds(config.order),
      hidden: this.normalizeIds(config.hidden),
      version: config.version,
    };
  }

  saveConfig(tableKey: string, config: TableColumnConfig): void {
    if (!tableKey) return;

    const allConfigs = this.readAll();
    allConfigs[this.buildScopedKey(tableKey)] = {
      order: this.normalizeIds(config.order),
      hidden: this.normalizeIds(config.hidden),
      version: CONFIG_VERSION,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
  }

  applyConfig<T extends { id: string }>(
    tableKey: string,
    columns: readonly T[],
    fallbackHidden: readonly string[] = []
  ): TableColumnConfig & { columns: T[] } {
    const normalizedFallbackHidden = this.normalizeIds(fallbackHidden as string[]);
    const saved = this.getConfig(tableKey);

    const currentColumns = [...columns];
    const currentIds = currentColumns.map(column => this.normalizeId(column.id));

    const order = this.normalizeOrder(saved?.order ?? currentIds, currentIds);
    const hidden = this.normalizeHidden(saved, normalizedFallbackHidden, currentIds);

    const sortedColumns = this.sortColumnsByOrder(currentColumns, order);

    return {
      order,
      hidden,
      columns: sortedColumns,
    };
  }

  private sortColumnsByOrder<T extends { id: string }>(columns: readonly T[], order: readonly string[]): T[] {
    const indexMap = new Map(order.map((id, index) => [id, index]));

    return [...columns].sort((a, b) => {
      const aIndex = indexMap.get(this.normalizeId(a.id)) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = indexMap.get(this.normalizeId(b.id)) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
  }

  private normalizeHidden(
    saved: StoredTableColumnConfig | null,
    fallbackHidden: readonly string[],
    availableIds: readonly string[]
  ): string[] {
    const available = new Set(availableIds);
    if (saved?.hidden == null) return this.normalizeIds(fallbackHidden).filter(id => available.has(id));

    // Konfigurationen vor CONFIG_VERSION listen die Standard-Ausblendungen nicht auf, weil das
    // Grid sie ohnehin unbedingt versteckt hat. Einmalig einmischen, damit das Update nicht
    // ploetzlich ID-Spalten einblendet.
    const source =
      saved.version === CONFIG_VERSION ? saved.hidden : [...saved.hidden, ...fallbackHidden];

    return this.normalizeIds(source).filter(id => available.has(id));
  }

  private normalizeOrder(orderFromConfig: readonly string[], availableIds: readonly string[]): string[] {
    const available = new Set(availableIds);
    const validConfigured = this.normalizeIds(orderFromConfig).filter(id => available.has(id));
    const missing = availableIds.filter(id => !validConfigured.includes(id));
    return [...validConfigured, ...missing];
  }

  private normalizeIds(ids: readonly string[] | undefined): string[] {
    if (!ids?.length) return [];

    const unique = new Set<string>();
    ids.forEach(id => unique.add(this.normalizeId(id)));
    return [...unique];
  }

  private normalizeId(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private buildScopedKey(tableKey: string): string {
    const db = (localStorage.getItem('DB') ?? localStorage.getItem('FormatCurDB') ?? 'default').trim().toLowerCase();
    const user = (
      sessionStorage.getItem('User') ??
      localStorage.getItem('loginEmail') ??
      localStorage.getItem('username') ??
      'anonymous'
    )
      .trim()
      .toLowerCase();

    return [db, user, tableKey.trim().toLowerCase()].join('::');
  }

  private readAll(): Record<string, StoredTableColumnConfig> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return Object.create(null) as Record<string, StoredTableColumnConfig>;

    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ?
          parsed
        : (Object.create(null) as Record<string, StoredTableColumnConfig>);
    } catch {
      return Object.create(null) as Record<string, StoredTableColumnConfig>;
    }
  }
}
